/**
 * /it/trascrizione-audio — pillar reframe bridge (Italian).
 *
 * Targets trascrizione audio (3.5k) / trascrivere audio in testo (3.3k).
 * Reframe: la trascrizione è materia prima, non il risultato.
 */
import { MarketingShell } from './MarketingShell';

export function ItTrascrizioneAudio() {
  return (
    <MarketingShell
      currentLocale="it"
      hreflangPath="/trascrizione-audio"
      hreflangLocales={['it']}
      documentTitle="Trascrizione audio in testo: gratis e veloce (e poi?) · Draftlize"
      documentDescription="Come trascrivere l'audio in testo gratis e con buona precisione — più il passo che tutti dimenticano: estrarre le decisioni che contano davvero."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Guida · Trascrizione</span>
          <h1 className="serif" style={{ fontSize: 'clamp(34px, 4.6vw, 56px)', lineHeight: 1.05, margin: '12px 0 24px' }}>
            Trascrizione audio —<br />
            <em>il testo è l'inizio, non la fine.</em>
          </h1>
          <p className="lede">
            Carichi l'audio e in pochi secondi hai una trascrizione pulita. Strumenti come Notta, Otter o tl;dv sono ottimi nel catturare ciò che è stato detto. Continua a usarli. Ma una trascrizione è <em>materia prima</em>, non il risultato. Ciò che contava davvero in quella riunione — la <em>decisione</em> presa, e perché — sparisce in migliaia di parole, finisce in un documento che nessuno riapre ed è obsoleto in poche settimane. Questa pagina parla del passo dopo la trascrizione.
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Come trascrivere l'audio in testo, gratis.</h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <ol style={{ fontSize: 16, lineHeight: 2, color: 'var(--ink-1)', margin: 0, paddingLeft: 22 }}>
            <li><strong>Scegli il file</strong> — MP3, M4A, WAV o un messaggio vocale.</li>
            <li><strong>Trascrivi</strong> — uno strumento IA converte la voce in testo, di solito con una quota gratuita.</li>
            <li><strong>Correggi</strong> — ricontrolla nomi e termini, poi esporta.</li>
          </ol>
          <p style={{ fontSize: 17, lineHeight: 1.8, color: 'var(--ink-1)', margin: '22px 0 0' }}>
            Funziona per file audio, messaggi vocali e MP3. Il punto: più la trascrizione è precisa, più grande è il muro di testo — e più in profondità resta sepolta l'unica decisione che conta. Catturare non è più il collo di bottiglia. <em>Estrarre</em> lo è.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">Materia prima vs contesto vivo</span>
            <h2 className="serif">Dove finisce la trascrizione, <em>inizia il lavoro.</em></h2>
          </div>
          <p>Il tuo strumento di trascrizione e Draftlize fanno lavori diversi. Uno cattura la conversazione; l'altro tiene vive le decisioni che ne escono. Tieni entrambi.</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th>Strumento di trascrizione</th>
              <th className="ours">Draftlize</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Ruolo</td><td>Catturare ciò che è stato detto</td><td className="ours yes">Tenere aggiornato ciò che è stato deciso</td></tr>
            <tr><td>Output</td><td className="partial">Trascrizione + riassunto</td><td className="ours yes">Schede di decisione in un grafo</td></tr>
            <tr><td>Una decisione ne ribalta un'altra</td><td className="no">Due note slegate</td><td className="ours yes">Collegate; la vecchia diventa «stale»</td></tr>
            <tr><td>Tre settimane dopo</td><td className="no">Sepolta in un documento</td><td className="ours yes">Indirizzabile, attuale o segnalata</td></tr>
            <tr><td>Un agente IA può usarla</td><td className="partial">Legge testo piatto</td><td className="ours yes">Legge e scrive via MCP</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">Trasforma le decisioni in <em>contesto vivo.</em></h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            Lascia la cattura allo strumento. Ciò che conta è cosa succede alle poche decisioni nel testo — in Draftlize smettono di essere righe in un riassunto e diventano un grafo che resta vero. Hai un PDF da sintetizzare? Stesso principio: <a href="/it/riassunto-pdf" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>riassunto PDF con l'AI</a>.
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Le decisioni diventano schede, non righe</h3>
            <p>Incolla un riassunto e le decisioni al suo interno diventano schede tipizzate — la decisione, la motivazione, le alternative valutate, il responsabile. L'ora di conversazione si riduce a ciò che deve sopravviverle, ognuna indirizzabile per ID.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Una nuova decisione segnala la vecchia</h3>
            <p>La riunione di questa settimana ribalta una decisione del mese scorso. Essendo collegate, la nuova mette la vecchia — e ogni spec che vi poggia — automaticamente in «stale». La prossima riunione non rinegozia ciò che è già deciso.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Il tuo agente IA legge il deciso</h3>
            <p>Le decisioni diventano contesto che Claude Code o Cursor leggono via MCP — l'agente che scrive la prossima spec sa già cosa ha deciso il team martedì, e perché.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Il tuo strumento trascrive la riunione alla perfezione. Solo che non può dirti, tre settimane dopo, quali delle decisioni al suo interno valgono ancora.
        </blockquote>
        <cite>Tieni lo strumento di trascrizione. Lascia che Draftlize tenga vive le decisioni.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Domande frequenti.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: 'Qual è il miglior strumento gratuito per trascrivere?', a: 'Notta, Otter, tl;dv — scegli quello che ti è comodo. Draftlize non trascrive: interviene quando la trascrizione esiste, per estrarne le decisioni e tenerle aggiornate.' },
            { q: 'Quanto è precisa la trascrizione?', a: 'La trascrizione IA è ormai valida anche in italiano. Ma più è precisa, più grande è il muro di testo — perciò conta il passo di estrarre le decisioni.' },
            { q: 'Posso trascrivere messaggi vocali e MP3?', a: 'Sì, la maggior parte degli strumenti li gestisce. Il problema non è il formato, ma cosa fai dopo: il valore sta in poche decisioni, ed è quello che Draftlize conserva.' },
            { q: 'Cosa ci faccio con la trascrizione?', a: 'Incolla il riassunto in Draftlize e cattura le decisioni come schede — decisione, motivazione, responsabile. Non l\'intera trascrizione, ma le poche decisioni che devono sopravvivere alla riunione.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">La trascrizione diventa decisioni <em>— gratis con $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>Registrati e ricevi $5</span>
          <span>Paghi solo ciò che usi</span>
          <span>Il saldo non scade mai</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Tieni il tuo strumento di trascrizione. Lascia il riassunto in Draftlize e le decisioni al suo interno diventano schede vive — collegate, attuali e leggibili dal tuo agente IA.
          </p>
          <a className="btn-primary" href="/">Inizia gratis con $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
